// Every call the browser makes to the control plane, and what a refusal means
// (#231).
//
// **There is no token here and nothing is stored.** The session is a cookie
// the server sets and the browser cannot read — `torquenext_session`, HttpOnly,
// Secure, SameSite Lax (`control_plane/routes/auth.py`) — so the whole of the
// SPA's authentication is `credentials: "same-origin"` on the request, and the
// SPA is served from the same origin as the API (`control_plane/api.py`: the
// API's routes first, the page's catch-all last). A sign-in writes no state on
// this side; `GET /auth/session` is how the console learns who it is.
//
// What this file does own is the other half: turning a refusal into something a
// caller can branch on. `ApiError` carries the status, because the two statuses
// that decide which screen you see — 401 and 409 — are carried by the status
// and by nothing in the body.

/** A call the API refused, or that answered a body that is not JSON. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`the API answered ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * The line to show a person, out of the body FastAPI wrapped the refusal in.
 *
 * `HTTPException(detail=...)` is answered as `{"detail": <detail>}`, and this
 * codebase raises both shapes: `routes/auth.py` maps its `AuthError` family to
 * `{"error": code, "detail": message}` and `routes/records.py` raises a plain
 * string. Both are read; anything else is null rather than `[object Object]`.
 */
export function detailOf(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail !== null) {
    const inner = (detail as { detail?: unknown }).detail;
    if (typeof inner === "string") return inner;
    const code = (detail as { error?: unknown }).error;
    if (typeof code === "string") return code;
  }
  return null;
}

/**
 * Refusals the API words for a developer, in the words a person reads (#798).
 *
 * Keyed by what `detailOf` reads: an auth refusal's code where it carries no message, or
 * the whole of a refusal whose text is itself terse. The API's answer is unchanged;
 * `detailOf` still reads it as sent, for a caller that branches on the code.
 */
const REFUSAL_IN_WORDS = new Map<string, string>([
  ["invalid_credentials", "That did not match. Check what you entered and try again."],
  ["invalid email or password", "That email and password do not match. Check them and try again."],
  ["not_authenticated", "You are signed out. Sign in again."],
  ["select a workspace first", "Choose a workspace first."],
  ["this session has selected no workspace", "Choose a workspace first."],
]);

/** A bare `snake_case` code: an answer meant for a program, not for a person. */
const BARE_CODE = /^[a-z]+(?:_[a-z]+)+$/;

/** A sentence for any failure, refusal or not, so no screen shows a blank error. */
export function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    const said = detailOf(error.body);
    if (said === null) return `The API answered ${error.status}.`;
    const words = REFUSAL_IN_WORDS.get(said);
    if (words !== undefined) return words;
    return BARE_CODE.test(said) ? "The request was refused." : said;
  }
  if (error instanceof Error && error.message !== "") return error.message;
  return "The request did not complete.";
}

/** What a form says beside an address field the API refused (#1170). */
export const NOT_AN_ADDRESS = "That is not an email address. Check it and try again.";

/**
 * The sentence for an address field, when `error` is the API's 422 naming that body field.
 *
 * The rule is the server's (#1159): this reads its refusal, FastAPI's validation answer
 * `{"detail": [{"loc": ["body", <field>], ...}]}`, and judges no address itself. Null for
 * any other failure, which the form shows with `messageOf` as before.
 */
export function addressRefusal(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  if (typeof error.body !== "object" || error.body === null) return null;
  const detail = (error.body as { detail?: unknown }).detail;
  if (!Array.isArray(detail)) return null;
  const names = detail.some((entry) => {
    const loc = (entry as { loc?: unknown } | null)?.loc;
    return Array.isArray(loc) && loc.length === 2 && loc[0] === "body" && loc[1] === field;
  });
  return names ? NOT_AN_ADDRESS : null;
}

/** The slice of `fetch` this client uses, so a test can hand in a fake. */
export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface Api {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  /**
   * A partial write: the keys sent are the keys changed, and a key left out is
   * left alone. The first is `PATCH /api/v1/integrations/{id}` (#580), where the
   * distinction is the point — a body carrying no `credential` is what keeps a
   * connection's sealed token from being replaced by whatever a form re-sent.
   */
  patch<T>(path: string, body?: unknown): Promise<T>;
  /**
   * A delete, with a JSON body when one is given: `DELETE /auth/passkeys/{id}`
   * takes its step-up grant there, as every step-up-gated route does (#661).
   */
  delete<T>(path: string, body?: unknown): Promise<T>;
}

async function answer<T>(response: Response): Promise<T> {
  // Read as text first: `POST /auth/logout` answers 204 with no body at all,
  // and `response.json()` on an empty body throws a `SyntaxError` that would
  // reach the caller as a crash rather than as a completed sign-out.
  const text = await response.text();
  let body: unknown = null;
  if (text !== "") {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = text;
    }
  }
  if (!response.ok) throw new ApiError(response.status, body);
  // The control plane serves the SPA's own page behind every path it does not
  // claim, with a 200 (`control_plane/api.py`). A client that asked for JSON
  // and got the page back has hit a route that does not exist, and must say so
  // rather than hand a string to a screen expecting rows (#417 found this in a
  // browser: `e.map is not a function`).
  if (typeof body === "string" && /^\s*<!doctype html/i.test(body)) {
    throw new ApiError(404, { detail: "the API has no such route; the page came back instead" });
  }
  return body as T;
}

export function createApi(doFetch: Fetch, baseUrl = ""): Api {
  const send = (path: string, init: RequestInit) =>
    doFetch(`${baseUrl}${path}`, { credentials: "same-origin", ...init });
  return {
    get: <T,>(path: string) => send(path, { method: "GET" }).then(answer<T>),
    post: <T,>(path: string, body?: unknown) =>
      send(path, {
        method: "POST",
        headers: body === undefined ? undefined : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(answer<T>),
    put: <T,>(path: string, body?: unknown) =>
      send(path, {
        method: "PUT",
        headers: body === undefined ? undefined : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(answer<T>),
    patch: <T,>(path: string, body?: unknown) =>
      send(path, {
        method: "PATCH",
        headers: body === undefined ? undefined : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(answer<T>),
    delete: <T,>(path: string, body?: unknown) =>
      send(path, {
        method: "DELETE",
        headers: body === undefined ? undefined : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(answer<T>),
  };
}

/** The one the page uses: the browser's own `fetch`, against this origin. */
export const browserApi: Api = createApi((input, init) => fetch(input, init));
